/**
 * Livello di normalizzazione: traduce i payload grezzi di Motornet nei tipi di
 * dominio consumati dalla UI.
 *
 * Costruisce il {@link Vehicle} (anagrafica, prezzi, foto, scheda tecnica) e il
 * {@link Catalog} degli accessori (opzioni normalizzate, indici e membri dei
 * pacchetti), isolando la UI dalle disomogeneità e dal vocabolario dei dati
 * Motornet.
 */
import {
	groupByMacrogruppo,
	resolveFormulaAccessories,
	findBondedColourPairs,
	flattenAccessories,
	RENDERED_BUCKETS,
	type WorkingAccessory,
} from './configurator';
import type {
	ConfigurableStockVehicle,
	MotornetCodeLabel,
	MotornetModel,
	MotornetVehicleKind,
	MotornetVehiclePayload,
	PlatformConfigurableListItem,
	PlatformConfigurableMake,
	StockVehicle,
	StockVehicleSpecs,
} from 'src/motornet/types';
import { NOLEGGIATORI_QUOTATORE } from 'src/utils/constants';
import type {
	BodyType,
	Catalog,
	CatalogOption,
	CategoryGroup,
	Make,
	Model,
	SpecRow,
	Trim,
	Vehicle,
	VehicleSpecs,
} from './domain';

/** Risultato completo di {@link normalize}: veicolo, catalogo e payload originale. */
export type NormalizedVehicleModel = {
	vehicle: Vehicle;
	catalog: Catalog;
	payload: MotornetVehiclePayload;
};

/** Normalizza una marca della piattaforma nel tipo di dominio {@link Make}. */
export const normalizeMake = (item: PlatformConfigurableMake): Make => ({
	make: item.nome,
	code: item.acronimo,
	logoUrl: item.logo ?? '',
});

/** Forma d'ingresso (parziale) da cui {@link normalizeModel} ricava un {@link Model}. */
export type ConfigurableModelInput = {
	make?: { code: string; name: string } | null;
	model?: { code: string; description: string };
	image?: string | null;
	minPrice?: number | null;
	bodyType?: string | null;
	fuels?: string[];
};

/** Normalizza un modello configurabile nel tipo di dominio {@link Model}. */
export const normalizeModel = (item: ConfigurableModelInput): Model => ({
	make: item.make?.name ?? '',
	model: item.model?.description ?? '',
	modelCode: item.model?.code ?? '',
	img: item.image ?? '',
	fuels: (item.fuels ?? []).map((f) => f?.toUpperCase()),
	minPrice: item.minPrice ?? 0,
	bodyType: item.bodyType?.toUpperCase() as BodyType,
});

/** Normalizza una voce di listino allestimenti nel tipo di dominio {@link Trim}. */
export const normalizeTrim = (
	item: PlatformConfigurableListItem,
	vehicleKind: MotornetVehicleKind = 'auto'
): Trim => ({
	id: item.id ?? item.vehicleCode ?? item.manufactureCode ?? '',
	make: item.make ?? '',
	model: item.model ?? '',
	name: item.trim ?? '',
	listPrice: item.listPrice ?? 0,
	img: item.image ?? '',
	fuel: item.fuel ?? '',
	eurotaxCode: item.vehicleCode ?? '',
	vehicleKind,
});

/** Nome di un accessorio: la prima descrizione non vuota disponibile. */
const accessoryName = (a: WorkingAccessory): string =>
	(a.descrizione || a.descrizioneNormalizzata || a.codice || '').trim();

/** Sottotitolo dell'accessorio, solo se distinto dal nome. */
const accessorySubtitle = (a: WorkingAccessory): string => {
	const subtitle = (a.descrizioneNormalizzata ?? '').trim();
	return subtitle && subtitle !== accessoryName(a) ? subtitle : '';
};

/** Tooltip dell'accessorio (descrizione breve), solo se distinto dal nome. */
const accessoryTooltip = (a: WorkingAccessory): string | null => {
	const text = (a.descrizioneBreve ?? '').trim();
	return text && text !== accessoryName(a) ? text : null;
};

/** Estrae la descrizione da una coppia codice/etichetta Motornet. */
const label = (v: MotornetCodeLabel | null | undefined): string =>
	v?.descrizione ?? '';

/**
 * Costruisce una riga di scheda tecnica, oppure `null` se il valore è assente
 * (verrà scartata da {@link present}). Accoda l'unità di misura se fornita.
 */
const row = (
	id: string,
	labelKey: string,
	labelText: string,
	value: string | number | null | undefined,
	unit?: string
): SpecRow | null => {
	if (value === null || value === undefined || value === '') return null;
	return {
		id,
		labelKey,
		label: labelText,
		value: unit ? `${value} ${unit}` : String(value),
	};
};

/** Rimuove le righe assenti (null), mantenendo solo quelle valorizzate. */
const present = (rows: (SpecRow | null)[]): SpecRow[] =>
	rows.filter((r): r is SpecRow => r !== null);

/** Righe della sezione "Motore" della scheda tecnica. */
const buildEngine = (m: MotornetModel): SpecRow[] =>
	present([
		row(
			'displacement',
			'car_configurator_customizer_displacement',
			'Cilindrata',
			m.cilindrata,
			'cc'
		),
		row(
			'configuration',
			'car_configurator_customizer_engine_configuration',
			'Configurazione',
			m.architettura ? label(m.architettura) : m.nomeMotore
		),
		row(
			'power',
			'car_configurator_customizer_power',
			'Potenza',
			m.kw && m.hp ? `${m.kw} kW / ${m.hp} CV` : null
		),
		row(
			'torque',
			'car_configurator_customizer_torque',
			'Coppia',
			m.coppiaTotale || m.coppia
		),
		row(
			'gearbox',
			'car_configurator_customizer_gearbox',
			'Cambio',
			m.nomeCambio || label(m.cambio)
		),
		row(
			'drivenWheels',
			'car_configurator_customizer_driven_wheels',
			'Trazione',
			m.nomeTrazione || label(m.trazione)
		),
	]);

/** Righe della sezione "Prestazioni" (velocità, accelerazione). */
const buildPerformance = (m: MotornetModel): SpecRow[] =>
	present([
		row(
			'maxSpeed',
			'car_configurator_customizer_max_speed',
			'Velocità massima',
			m.velocita,
			'km/h'
		),
		row(
			'acceleration',
			'car_configurator_customizer_acceleration',
			'Accelerazione 0-100 km/h',
			m.accelerazione,
			's'
		),
	]);

/** Righe della sezione "Dimensioni e pesi". */
const buildDimensions = (m: MotornetModel): SpecRow[] =>
	present([
		row(
			'length',
			'car_configurator_customizer_length',
			'Lunghezza',
			m.lunghezza,
			'mm'
		),
		row(
			'width',
			'car_configurator_customizer_width',
			'Larghezza',
			m.larghezza,
			'mm'
		),
		row(
			'height',
			'car_configurator_customizer_height',
			'Altezza',
			m.altezza,
			'mm'
		),
		row(
			'wheelbase',
			'car_configurator_customizer_wheelbase',
			'Passo',
			m.passo,
			'mm'
		),
		row(
			'cargo',
			'car_configurator_customizer_cargo',
			'Capacità bagagliaio',
			m.bagagliaio,
			'l'
		),
		row(
			'curbWeight',
			'car_configurator_customizer_curb_weight',
			'Peso a vuoto',
			m.pesoVuoto,
			'kg'
		),
		row('seats', 'car_configurator_customizer_seats', 'Posti', m.posti),
	]);

/** Righe della sezione "Consumi e autonomia" (inclusa batteria per gli EV). */
const buildConsumption = (m: MotornetModel): SpecRow[] =>
	present([
		row(
			'consumptionCombined',
			'car_configurator_customizer_consumption_combined',
			'Consumo combinato',
			m.consumoMedio,
			'l/100km'
		),
		row(
			'range',
			'car_configurator_customizer_range',
			'Autonomia',
			m.autonomiaMedia,
			'km'
		),
		row(
			'battery',
			'car_configurator_customizer_battery',
			'Capacità batteria',
			m.capacitaNettaBatteria,
			'kWh'
		),
	]);

/** Righe della sezione "Emissioni" (CO2, classe Euro). */
const buildEmissions = (m: MotornetModel): SpecRow[] =>
	present([
		row(
			'co2Combined',
			'car_configurator_customizer_co2_combined',
			'Emissioni CO2',
			m.emissioniCo2,
			'g/km'
		),
		row(
			'emissionLevel',
			'car_configurator_customizer_emission_level',
			'Classe di emissioni',
			m.euro
		),
	]);

/** Dotazione di serie come righe di scheda tecnica (etichetta senza valore). */
const buildEquipment = (all: WorkingAccessory[]): SpecRow[] =>
	all
		.filter((a) => a.tipoAccessorio === 'serie')
		.map((a, i) => ({
			id: `equip-${i}`,
			label: accessoryName(a),
			value: '',
			optionId: a.idAccessorio,
		}))
		.filter((r) => r.label !== '');

/** Assembla l'intera scheda tecnica dalle singole sezioni. */
const buildSpecs = (
	m: MotornetModel,
	all: WorkingAccessory[]
): VehicleSpecs => ({
	equipment: buildEquipment(all),
	performance: buildPerformance(m),
	engine: buildEngine(m),
	dimensions: buildDimensions(m),
	consumption: buildConsumption(m),
	emissions: buildEmissions(m),
});

/**
 * Scheda tecnica di un veicolo di stock.
 */
export function buildStockSpecs(s: StockVehicleSpecs): VehicleSpecs {
	return {
		equipment: [],
		engine: present([
			row(
				'displacement',
				'car_configurator_customizer_displacement',
				'Cilindrata',
				s.cilindrata,
				'cc'
			),
			row(
				'configuration',
				'car_configurator_customizer_engine_configuration',
				'Configurazione',
				s.motore
			),
			row(
				'power',
				'car_configurator_customizer_power',
				'Potenza',
				s.kw && s.cavalli
					? `${s.kw} kW / ${s.cavalli} CV`
					: s.cavalli
					? `${s.cavalli} CV`
					: null
			),
			row('torque', 'car_configurator_customizer_torque', 'Coppia', s.coppia),
			row('gearbox', 'car_configurator_customizer_gearbox', 'Cambio', s.cambio),
			row(
				'drivenWheels',
				'car_configurator_customizer_driven_wheels',
				'Trazione',
				s.trazione
			),
		]),
		performance: present([
			row(
				'maxSpeed',
				'car_configurator_customizer_max_speed',
				'Velocità massima',
				s.velocita,
				'km/h'
			),
			row(
				'acceleration',
				'car_configurator_customizer_acceleration',
				'Accelerazione 0-100 km/h',
				s.accelerazione,
				's'
			),
		]),
		dimensions: present([
			row(
				'length',
				'car_configurator_customizer_length',
				'Lunghezza',
				s.lunghezza,
				'cm'
			),
			row(
				'width',
				'car_configurator_customizer_width',
				'Larghezza',
				s.larghezza,
				'cm'
			),
			row(
				'height',
				'car_configurator_customizer_height',
				'Altezza',
				s.altezza,
				'cm'
			),
			row(
				'wheelbase',
				'car_configurator_customizer_wheelbase',
				'Passo',
				s.passo,
				'cm'
			),
			row(
				'cargo',
				'car_configurator_customizer_cargo',
				'Capacità bagagliaio',
				s.capacitaVanoCarico,
				'l'
			),
			row('seats', 'car_configurator_customizer_seats', 'Posti', s.posti),
		]),
		consumption: present([
			row(
				'consumptionCombined',
				'car_configurator_customizer_consumption_combined',
				'Consumo combinato',
				s.consumoMedio,
				'l/100km'
			),
			row(
				'consumptionUrban',
				'car_configurator_customizer_consumption_urban',
				'Consumo urbano',
				s.consumoUrbano,
				'l/100km'
			),
			row(
				'consumptionExtraUrban',
				'car_configurator_customizer_consumption_extra_urban',
				'Consumo extraurbano',
				s.consumoExtraUrbano,
				'l/100km'
			),
		]),
		emissions: present([
			row(
				'co2Combined',
				'car_configurator_customizer_co2_combined',
				'Emissioni CO2',
				s.emissioniCo2,
				'g/km'
			),
			row(
				'emissionLevel',
				'car_configurator_customizer_emission_level',
				'Classe di emissioni',
				s.euro != null ? `Euro ${s.euro}` : null
			),
		]),
	};
}

/** Foto principale del veicolo: prima immagine del payload, con fallback sul modello. */
const heroPhotoOf = (payload: MotornetVehiclePayload): string =>
	payload.images?.[0]?.url ?? payload.details?.modello?.immagine ?? '';

/**
 * Costruisce il {@link Vehicle} base dal payload: anagrafica, prezzi (base netto,
 * listino, messa su strada), foto e scheda tecnica. Non include gli accessori
 * scelti — quelli vivono nel {@link Catalog} e nell'engine.
 */
export function buildVehicle(payload: MotornetVehiclePayload): Vehicle {
	const m = payload.details.modello;
	const price = payload.currentPriceDetails;
	const all = flattenAccessories(payload.accessories);

	return {
		ref: {
			eurotaxCode: payload.eurotaxCode,
			vehicleKind: payload.vehicleKind,
			year: payload.year,
			month: payload.month,
		},
		make: m.marca?.nome ?? '',
		model: m.modello ?? '',
		version: m.nomeAllestimento || m.allestimento || '',
		modelYear: m.inizioProduzione ?? '',
		bodyType: m.modelloBreveCarrozzeria?.descrizione ?? m.nomeCarrozzeria ?? '',
		doors: String(m.porte ?? ''),
		drivenWheels: m.nomeTrazione || label(m.trazione),
		fuelType: m.alimentazione?.codice ?? '',
		fuelLabel: label(m.alimentazione),
		prices: {
			currency: 'EUR',
			basePrice: price?.prezzoListinoNetto ?? 0,
			retailPrice: price?.prezzoListino ?? m.prezzoListino ?? 0,
			roadPrice: price?.messaSustrada ?? 0,
			displayPrice: '',
		},
		photos: (payload.images ?? []).map((i) => i.url),
		heroPhoto: heroPhotoOf(payload),
		specs: buildSpecs(m, all),
	};
}

/**
 * Costruisce il {@link Catalog} degli accessori dal payload: normalizza ogni
 * accessorio in una {@link CatalogOption}, calcola i membri dei pacchetti e
 * prepara gli indici (per id, per idDizionario, per bucket, per categoria).
 */
export function buildCatalog(payload: MotornetVehiclePayload): Catalog {
	const all = flattenAccessories(payload.accessories);

	const packMembers = new Map<number, number[]>();
	const memberIds = new Set<number>();
	for (const a of all) {
		if (a.tipoAccessorio !== 'pacchetti') continue;
		// solo per la visualizzazione: la risoluzione non-strict continua a
		// elencare i membri della prima alternativa OR invece di nascondere il
		// contenuto non ancora scelto
		const members = resolveFormulaAccessories(
			a.formulaInclusioneStrutturata,
			all,
			new Set(),
			{ strict: false }
		).map((x) => x.idAccessorio);
		packMembers.set(a.idAccessorio, members);
		for (const id of members) memberIds.add(id);
	}

	// Vernici "sempre insieme": collassiamo ogni coppia master/gemello in una sola
	// voce. Il gemello (prezzo 0) viene tolto dalla lista renderizzata; il master
	// ne conserva l'id per riferimento. Il gemello resta nel payload grezzo, così
	// l'engine continua a trascinarlo via `formulaInclusione` alla selezione.
	const colourPairs = findBondedColourPairs(
		all.filter((a) => a.tipoAccessorio === 'vernici')
	);
	const twinByMaster = new Map<number, WorkingAccessory>(
		colourPairs.map((p) => [p.master.idAccessorio, p.twin])
	);
	const collapsedTwinIds = new Set(colourPairs.map((p) => p.twin.idAccessorio));

	const options: CatalogOption[] = all
		.filter(
			(a) =>
				(RENDERED_BUCKETS as readonly string[]).includes(a.tipoAccessorio) &&
				!collapsedTwinIds.has(a.idAccessorio)
		)
		.map((a, order) => {
			const twin = twinByMaster.get(a.idAccessorio);
			return {
				id: a.idAccessorio,
				dictionaryId: a.idDizionario,
				code: a.codice ?? '',
				name: accessoryName(a),
				subtitle: accessorySubtitle(a),
				tooltip: accessoryTooltip(a),
				bucket: a.tipoAccessorio,
				isPack: a.tipoAccessorio === 'pacchetti',
				isColour: a.tipoAccessorio === 'vernici',
				isStandard: a.tipoAccessorio === 'serie',
				category: a.macrogruppo || 'Altro',
				categoryKey: a.codiceMacrogruppo || a.macrogruppo || 'altro',
				retailPrice: a.prezzoListino ?? 0,
				basePrice: a.prezzoListinoNetto ?? 0,
				displayPrice: '',
				isPackMember: memberIds.has(a.idAccessorio),
				twinId: twin?.idAccessorio,
				twinDictionaryId: twin?.idDizionario,
				twinCode: twin?.codice ?? undefined,
				twinName:
					twin && accessoryName(twin) !== accessoryName(a)
						? accessoryName(twin)
						: undefined,
				availability: a.codiceDisponibilita ?? '',
				order,
			};
		});

	const byId = new Map(options.map((o) => [o.id, o]));

	const byDictionaryId = new Map<number, CatalogOption[]>();
	for (const o of options) {
		const bucket = byDictionaryId.get(o.dictionaryId) ?? [];
		bucket.push(o);
		byDictionaryId.set(o.dictionaryId, bucket);
	}

	const grouped = groupByMacrogruppo(
		all.filter((a) => a.tipoAccessorio === 'optionals')
	);
	const categories: CategoryGroup[] = Object.entries(grouped).map(
		([key, items]) => ({
			key,
			name: key,
			options: items
				.map((a) => byId.get(a.idAccessorio))
				.filter((o): o is CatalogOption => !!o),
		})
	);

	return {
		options,
		byId,
		byDictionaryId,
		byBucket: {
			colors: options.filter((o) => o.isColour),
			options: options.filter((o) => o.bucket === 'optionals'),
			packs: options.filter((o) => o.isPack),
			standard: options.filter((o) => o.isStandard),
		},
		categories,
		packMembers,
	};
}

/**
 * Punto d'ingresso della normalizzazione: da un payload Motornet produce
 * veicolo e catalogo di dominio, conservando il payload originale per gli usi
 * che richiedono i dati grezzi (es. costruzione delle configurazioni).
 */
export function normalize(
	payload: MotornetVehiclePayload
): NormalizedVehicleModel {
	return {
		vehicle: buildVehicle(payload),
		catalog: buildCatalog(payload),
		payload,
	};
}

const roundCents = (value: number): number => Math.round(value * 100) / 100;

/**
 * Lookup dei noleggiatori: in HMP `noleggiatore` convive in due vocabolari a
 * seconda di quando il veicolo è stato scritto — codice minuscolo ("arval",
 * "vw_leasing") e nome visualizzato ("Arval", "VW Leasing", "ALD"). Si indicizza
 * su entrambi, normalizzati, così il lookup è uno solo.
 */
const NOLEGGIATORE_BY_ALIAS: Map<string, string> = (() => {
	const fold = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, '');
	const map = new Map<string, string>();
	for (const item of Object.values(NOLEGGIATORI_QUOTATORE)) {
		map.set(fold(item.code), item.code);
		map.set(fold(item.name), item.code);
	}
	return map;
})();

/**
 * Codice alimentazione a partire dal codice di elettrificazione HMP.
 */
function fuelCodeOf(
	powertrainType: string | null,
	alimentazione: string | null
): string | null {
	switch (powertrainType) {
		case 'E':
		case 'R':
			return 'ELEC';
		case 'P':
			return 'PHEV';
		case 'M':
			return 'MHEV';
		case 'H':
			return 'IBRIP';
		default:
			break;
	}

	const v = (alimentazione ?? '').toLowerCase();
	if (!v) return null;
	if (v.includes('diesel') || v.includes('gasolio')) return 'DIES';
	if (
		v.includes('senza piombo') ||
		v.includes('benzina') ||
		v.includes('gpl') ||
		v.includes('metano')
	) {
		return 'BENZ';
	}
	if (v.includes('elettric')) return 'ELEC';
	return null;
}

export function toStockVehicle(raw: ConfigurableStockVehicle): StockVehicle {
	const dv = raw.dati_veicolo;
	const det = raw.dettagli_veicolo;

	return {
		id: raw.id,
		vehicleCode: dv.vehicle_code,
		jatoId: dv.jato_id,
		vehicleKind: dv.vehicle_kind,

		make: dv.marca,
		model: dv.modello,
		trim: dv.allestimento,
		modelYear: dv.model_year,
		fuelCode: fuelCodeOf(dv.powertrain_type, dv.alimentazione),

		price: {
			currency: raw.currency,
			listPriceNoVat: raw.prezzoListinoNetto,
			optionalsNoVat: raw.prezzoOptionalsNetto,
			totalNoVat: roundCents(raw.prezzoListinoNetto + raw.prezzoOptionalsNetto),
			extraScontoPerc: raw.extraScontoPerc,
		},

		optionals: raw.optionals.map((o) => ({
			code: o.codice_costruttore,
			description: o.descrizione,
			priceVat: o.valore,
			priceNoVat: o.valoreNetto,
		})),

		colors: {
			exteriorCode: det.codice_colore_esterno,
			exteriorLabel: det.colore_esterno,
			interiorCode: det.codice_colore_interno,
			interiorLabel: det.colore_interno,
		},

		specs: {
			alimentazione: dv.alimentazione,
			carrozzeria: dv.carrozzeria,
			cambio: dv.cambio,
			porte: dv.porte,
			posti: dv.posti,
			cavalli: dv.cavalli,
			motore: dv.motore,
			kw: dv.kw,
			cilindrata: dv.cilindrata,
			coppia: dv.coppia,
			trazione: dv.trazione,
			velocita: dv.velocita,
			accelerazione: dv.accelerazione,
			lunghezza: dv.lunghezza,
			larghezza: dv.larghezza,
			altezza: dv.altezza,
			passo: dv.passo,
			capacitaVanoCarico:
				dv.capacita_vano_carico != null
					? String(dv.capacita_vano_carico)
					: null,
			consumoMedio: dv.consumo_medio,
			consumoUrbano: dv.consumo_urbano,
			consumoExtraUrbano: dv.consumo_extra_urbano,
			emissioniCo2: dv.emissioni_co2,
			euro: dv.euro,
		},

		rentalCompanies: raw.lista_noleggiatori_quotatore.flatMap((n) => {
			const code = NOLEGGIATORE_BY_ALIAS.get(
				n.noleggiatore.toLowerCase().replace(/[^a-z0-9]+/g, '')
			);
			return code
				? [
						{
							code,
							enabled: n.is_checked,
							extraScontoPerc: n.extra_sconto_perc,
						},
				  ]
				: [];
		}),

		photos: raw.image_list.map((i) => i.url),
	};
}
